import { GameCore } from './GameCore';
import { useGameStore } from '../state/store';
import { connectToServer, getPlayerId, setOnReconnected } from '../net/netClient';
import { getScene } from './scene/scene';
import { getCamera } from './scene/camera';
import { setVertexProbe } from './debug/vertexProbe';

let gameCore: GameCore | null = null;

export type GameStartMode = 'online' | 'local';

/** Dev-only: expose the game core for scripted verification (Playwright). No-op in production. */
function exposeDevHandle(core: GameCore): void {
  if (import.meta.env.DEV) {
    const w = window as unknown as Record<string, unknown>;
    w.__worldifyGame = core;
    // Console perf probes: scene.overrideMaterial, renderer.info.render, camera pose.
    w.__worldifyScene = getScene();
    w.__worldifyCamera = getCamera();
    // Vertex-cost probe: all terrain attributes, trivial fragment (see debug/vertexProbe.ts).
    w.__worldifyVertexProbe = setVertexProbe;
  }
}

export async function createGame(mode: GameStartMode = 'online'): Promise<GameCore> {
  if (gameCore) {
    return gameCore;
  }

  if (mode === 'local') {
    // Offline: no server. Generate terrain client-side and un-gate the UI.
    useGameStore.getState().setUseServerChunks(false);
    useGameStore.getState().setConnectionStatus('connected');
    gameCore = new GameCore();
    await gameCore.init();
    gameCore.setLocalPlayerId(1); // synthetic id (no server assignment)
    exposeDevHandle(gameCore);
    return gameCore;
  }

  useGameStore.getState().setConnectionStatus('connecting');

  // Connect to server FIRST so WebSocket is ready for chunk requests
  await connectToServer();

  // Create and initialize game core (will request chunks from server)
  gameCore = new GameCore();
  await gameCore.init();

  // Set local player ID so GameCore knows which player to skip in snapshots
  const playerId = getPlayerId();
  if (playerId !== null) {
    gameCore.setLocalPlayerId(playerId);
  }

  // Set up reconnection handler to reset game state after reconnect
  setOnReconnected(() => {
    const newPlayerId = getPlayerId();
    if (gameCore && newPlayerId !== null) {
      console.log(`[game] Reconnected with new player ID: ${newPlayerId}`);
      gameCore.setLocalPlayerId(newPlayerId);
      // Reset game state for fresh start with new server
      gameCore.handleReconnect();
    }
  });

  exposeDevHandle(gameCore);
  return gameCore;
}

export function getGameCore(): GameCore | null {
  return gameCore;
}
